import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';

// Enable Immer support for Map and Set
enableMapSet();

/**
 * Message state for bulk sending
 */
export interface MessageState {
  clientId: string;
  clientName: string;
  jid: string;
  originalText: string;
  editedText: string | null;
  status: 'pending' | 'sending' | 'sent' | 'failed';
  error?: string;
}

/**
 * Bulk Send State Management
 * Manages client selection, message generation, editing, and sending status
 * Not persisted - resets after completion
 */
interface BulkSendState {
  // Client selection
  selectedClientIds: Set<string>;

  // Generated messages
  messages: Map<string, MessageState>;

  // Sending state
  sending: boolean;

  // Actions - Client Selection
  toggleClient: (id: string) => void;
  setClients: (ids: string[]) => void;
  clearSelection: () => void;
  selectAll: (ids: string[]) => void;

  // Actions - Messages
  setMessages: (messages: MessageState[]) => void;
  updateMessage: (clientId: string, editedText: string | null) => void;
  removeMessage: (clientId: string) => void;

  // Actions - Sending Status
  setSending: (sending: boolean) => void;
  updateStatus: (clientId: string, status: MessageState['status'], error?: string) => void;

  // Actions - Reset
  reset: () => void;
}

export const useBulkSendStore = create<BulkSendState>()(
  immer((set) => ({
    // Initial state
    selectedClientIds: new Set(),
    messages: new Map(),
    sending: false,

    // Client selection actions
    toggleClient: (id) => set((state) => {
      if (state.selectedClientIds.has(id)) {
        state.selectedClientIds.delete(id);
      } else {
        state.selectedClientIds.add(id);
      }
    }),

    setClients: (ids) => set((state) => {
      state.selectedClientIds = new Set(ids);
    }),

    clearSelection: () => set((state) => {
      state.selectedClientIds.clear();
    }),

    selectAll: (ids) => set((state) => {
      state.selectedClientIds = new Set(ids);
    }),

    // Message actions
    setMessages: (messages) => set((state) => {
      state.messages = new Map(messages.map(m => [m.clientId, m]));
    }),

    updateMessage: (clientId, editedText) => set((state) => {
      const message = state.messages.get(clientId);
      if (message) {
        message.editedText = editedText;
      }
    }),

    removeMessage: (clientId) => set((state) => {
      state.messages.delete(clientId);
      state.selectedClientIds.delete(clientId);
    }),

    // Sending status actions
    setSending: (sending) => set((state) => {
      state.sending = sending;
    }),

    updateStatus: (clientId, status, error) => set((state) => {
      const message = state.messages.get(clientId);
      if (message) {
        message.status = status;
        if (error) {
          message.error = error;
        }
      }
    }),

    // Reset all state
    reset: () => set((state) => {
      state.selectedClientIds.clear();
      state.messages.clear();
      state.sending = false;
    }),
  }))
);
